import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import type {
  CurationClassification,
  CurationItem,
  CurationStatus,
} from '~/components/Bedbank/Properties/Curation/types';

import BedbankService from '~/services/BedbankService';

export const curationQueryKeys = {
  all: ['curation'] as const,
  lists: () => [...curationQueryKeys.all, 'list'] as const,
  list: (filters: { page: number; status?: CurationStatus | ''; classification?: CurationClassification | '' }) =>
    [...curationQueryKeys.lists(), filters] as const,
  details: () => [...curationQueryKeys.all, 'detail'] as const,
  detail: (propertyId: string) => [...curationQueryKeys.details(), propertyId] as const,
};

interface CurationListFilters {
  page: number;
  pageSize: number;
  status: CurationStatus | '';
  classification: CurationClassification | '';
}

export function useGetCurationList({ page, pageSize, status, classification }: CurationListFilters) {
  return useQuery({
    queryKey: curationQueryKeys.list({ page, status, classification }),
    queryFn: async ({ signal }) => {
      const params: Record<string, unknown> = {
        page: page + 1,
        limit: pageSize,
      };
      if (status) {
        params.status = status;
      }
      if (classification) {
        params.classification = classification;
      }
      const response = await BedbankService.getCurationList(params);
      return { result: response.result as Array<CurationItem>, count: response.count as number };
    },
  });
}

export function useGetCurationDetail(propertyId: string) {
  return useQuery({
    queryKey: curationQueryKeys.detail(propertyId),
    queryFn: async () => {
      const response = await BedbankService.getCurationDetail(propertyId);
      return response.result as CurationItem;
    },
    enabled: !!propertyId,
  });
}

export function useCurateProperties() {
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (propertyIds: Array<string>) => BedbankService.curateProperties(propertyIds),
    onSuccess: ({ result }) => {
      enqueueSnackbar(`Queued ${result.queued} properties for curation`, { variant: 'success' });
    },
    onError: (err: Error) => {
      enqueueSnackbar(`Curation failed: ${err.message}`, { variant: 'error' });
    },
  });
}

export function useReviewCuration() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ propertyId, decision }: { propertyId: string; decision: 'approved' | 'rejected' }) =>
      BedbankService.reviewCuration(propertyId, decision),
    onSuccess: (_data, variables) => {
      const label = variables.decision === 'approved' ? 'approved and set live' : 'rejected';
      enqueueSnackbar(`Property ${label}`, { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: curationQueryKeys.all });
    },
    onError: (err: Error, variables) => {
      const action = variables.decision === 'approved' ? 'approve' : 'reject';
      enqueueSnackbar(`Failed to ${action}: ${err.message}`, { variant: 'error' });
    },
  });
}
